import type { TimedPoint } from "./replay-core";

interface Structure {
  latitude?: number;
  longitude?: number;
  altitude_m?: number;
  size_m?: number;
  structure_kind?: string;
  source_structure_id?: number | string;
  id?: number | string;
  yaw_deg?: number | null;
  pitch_deg?: number | null;
  roll_deg?: number | null;
}

const METERS_PER_DEG_LAT = 111_320;

function structureKey(structure: Structure): string | number | null {
  return structure.source_structure_id ?? structure.id ?? null;
}

function structureRadius(structure: Structure): number {
  return Math.max(4, Number(structure.size_m || 20) / 2);
}

/**
 * Per-checkpoint list of pass-through times (one entry per lap). A pass is
 * counted only when the drone actually crosses the gate plane within the disk
 * radius — not merely when it is near the gate. This prevents false positives
 * where the drone flies past the side of the ring without going through it.
 */
export type LapPassTimes = Map<string | number, number[]>;

/** Minimum seconds between two passes through the same gate. */
const PASS_COOLDOWN_S = 1.5;

export function computeLapPassTimes(
  structures: Structure[],
  points: TimedPoint[],
): LapPassTimes {
  const result: LapPassTimes = new Map();
  if (points.length < 2) return result;

  for (const structure of structures) {
    if (structure.structure_kind !== "checkpoint_ring") continue;
    if (structure.latitude == null || structure.longitude == null) continue;
    const key = structureKey(structure);
    if (key === null) continue;
    const radius = structureRadius(structure);
    const metersPerLonDeg =
      METERS_PER_DEG_LAT * Math.cos((structure.latitude * Math.PI) / 180);
    const gateAlt = structure.altitude_m ?? 0;

    const yawRaw = structure.yaw_deg;
    const hasYaw =
      yawRaw !== null && yawRaw !== undefined && !Number.isNaN(Number(yawRaw));
    const yawRad = hasYaw ? (Number(yawRaw) * Math.PI) / 180 : 0;
    const sinH = Math.sin(yawRad);
    const cosH = Math.cos(yawRad);

    const passes: number[] = [];
    let lastPassTime = -Infinity;

    if (hasYaw) {
      // Plane-crossing algorithm — treats the gate as an oriented disk with
      // normal vector (sinH, cosH, 0) in local ENU coords. A pass is registered
      // only when a telemetry segment crosses that plane within the disk radius.
      for (let i = 1; i < points.length; i += 1) {
        const p1 = points[i - 1];
        const p2 = points[i];
        const e1 = (p1.longitude - structure.longitude) * metersPerLonDeg;
        const n1 = (p1.latitude - structure.latitude) * METERS_PER_DEG_LAT;
        const u1 = (p1.altitude_m ?? gateAlt) - gateAlt;
        const e2 = (p2.longitude - structure.longitude) * metersPerLonDeg;
        const n2 = (p2.latitude - structure.latitude) * METERS_PER_DEG_LAT;
        const u2 = (p2.altitude_m ?? gateAlt) - gateAlt;

        // Signed distance to ring plane (positive = in front of gate along normal).
        const s1 = e1 * sinH + n1 * cosH;
        const s2 = e2 * sinH + n2 * cosH;
        const changedSign =
          (s1 > 0 && s2 <= 0) || (s1 < 0 && s2 >= 0) || (s1 === 0 && s2 !== 0);
        if (!changedSign) continue;

        const denom = s1 - s2;
        if (Math.abs(denom) < 1e-9) continue;
        const t = s1 / denom;
        if (t < 0 || t > 1) continue;

        const eC = e1 + t * (e2 - e1);
        const nC = n1 + t * (n2 - n1);
        const uC = u1 + t * (u2 - u1);

        // In-plane tangential offset (perpendicular to normal, horizontal) and
        // vertical offset combine into the disk distance from the ring center.
        const tC = eC * cosH - nC * sinH;
        const diskDist = Math.hypot(tC, uC);
        if (diskDist > radius) continue;

        const passTime =
          p1.elapsed_seconds + t * (p2.elapsed_seconds - p1.elapsed_seconds);
        if (passTime - lastPassTime < PASS_COOLDOWN_S) continue;
        passes.push(passTime);
        lastPassTime = passTime;
      }
    } else {
      // Fallback: no orientation info. Use a stricter radius check — the drone
      // must be well inside a sphere (not just the bounding cylinder) to count.
      // This reduces false positives when the drone skims past the ring.
      const strictRadius = radius * 0.75;
      let inside = false;
      for (const p of points) {
        const dlat = (p.latitude - structure.latitude) * METERS_PER_DEG_LAT;
        const dlon = (p.longitude - structure.longitude) * metersPerLonDeg;
        const du = (p.altitude_m ?? gateAlt) - gateAlt;
        const sphereDist = Math.hypot(dlat, dlon, du);
        const isHere = sphereDist <= strictRadius;
        if (isHere && !inside) {
          if (p.elapsed_seconds - lastPassTime >= PASS_COOLDOWN_S) {
            passes.push(p.elapsed_seconds);
            lastPassTime = p.elapsed_seconds;
          }
          inside = true;
        } else if (!isHere && inside) {
          inside = false;
        }
      }
    }
    if (passes.length) result.set(key, passes);
  }
  return result;
}

/** Highest lap index (1-based) the drone has reached at the given elapsed time. */
export function currentLapAt(
  lapPasses: LapPassTimes,
  elapsed: number,
): number {
  let maxLap = 0;
  lapPasses.forEach((times) => {
    let lap = 0;
    for (const t of times) {
      if (t <= elapsed) lap += 1;
      else break;
    }
    if (lap > maxLap) maxLap = lap;
  });
  return maxLap;
}

/** Per-checkpoint lap count completed at the given elapsed time. */
export function lapsPassedAt(
  lapPasses: LapPassTimes,
  elapsed: number,
): Map<string | number, number> {
  const counts = new Map<string | number, number>();
  lapPasses.forEach((times, key) => {
    let lap = 0;
    for (const t of times) {
      if (t <= elapsed) lap += 1;
      else break;
    }
    if (lap > 0) counts.set(key, lap);
  });
  return counts;
}

/**
 * Color cycle for laps. Lap 1 is the primary "passed" green, then we move
 * through warm/cool accents that read distinctly against the orange route.
 */
const LAP_COLORS = [
  "#4ADE80", // lap 1 — green
  "#FBBF24", // lap 2 — amber
  "#38BDF8", // lap 3 — cyan
  "#A78BFA", // lap 4 — violet
  "#F472B6", // lap 5 — pink
  "#F87171", // lap 6 — coral
];

export const CHECKPOINT_DEFAULT_COLOR = "#38BDF8";
export const CHECKPOINT_PENDING_COLOR = "#FF6B1A";

export function colorForLap(lap: number): string {
  if (lap <= 0) return CHECKPOINT_DEFAULT_COLOR;
  return LAP_COLORS[(lap - 1) % LAP_COLORS.length];
}
